// Google Cloud API Credit Service
// This service checks the remaining API credits from Google Cloud

interface CreditInfo {
  remaining: number;
  total: number;
  used: number;
  currency: string;
  lastUpdated: string;
}

export class CreditService {
  private static STORAGE_KEY = 'google_api_credit';
  private static UPDATE_INTERVAL = 60000; // Update every minute
  
  /**
   * Get current credit information
   * Note: Google Cloud doesn't provide a direct API to check credits in real-time
   * This is a simulation based on usage tracking
   */
  static async getCreditInfo(): Promise<CreditInfo> {
    try {
      // Get stored credit info
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const creditInfo = JSON.parse(stored);
        
        // Check if data is recent (less than 1 minute old)
        const lastUpdate = new Date(creditInfo.lastUpdated).getTime();
        const now = Date.now();
        
        if (now - lastUpdate < this.UPDATE_INTERVAL) {
          return creditInfo;
        }
      }
      
      // Simulate credit check (in production, this would call Google Cloud Billing API)
      // For now, we'll track based on usage
      const creditInfo = await this.calculateCreditsFromUsage();
      
      // Store the updated info
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(creditInfo));
      
      return creditInfo;
    } catch (error) {
      console.error('Error getting credit info:', error);
      
      // Return default values if error
      return {
        remaining: 0,
        total: 300, // Assuming $300 free trial credit
        used: 0,
        currency: 'USD',
        lastUpdated: new Date().toISOString()
      };
    }
  }
  
  /**
   * Calculate credits based on API usage
   * Pricing:
   * - Gemini API (AI Studio): FREE for text generation
   * - Google Cloud API: $0.002 per second for video generation
   */
  private static async calculateCreditsFromUsage(): Promise<CreditInfo> {
    // Get usage data from localStorage
    const usageData = this.getUsageData();
    
    // Calculate costs
    let totalCost = 0;
    
    // Text generation is FREE with Gemini API (AI Studio)
    // No cost for script generation
    
    // Video generation costs (8 seconds per video) - Google Cloud API only
    const videoCount = parseInt(localStorage.getItem('veo_video_count') || '0');
    const videoCost = videoCount * 8 * 0.002;  // $0.016 per 8-second video
    totalCost += videoCost;
    
    // Get initial credit (default $300 for free trial)
    const totalCredit = parseFloat(localStorage.getItem('google_cloud_credit_total') || '300');
    
    return {
      remaining: Math.max(0, totalCredit - totalCost),
      total: totalCredit,
      used: totalCost,
      currency: 'USD',
      lastUpdated: new Date().toISOString()
    };
  }
  
  /**
   * Get usage data from localStorage
   */
  private static getUsageData() {
    return {
      textCharacters: parseInt(localStorage.getItem('gemini_text_usage') || '0'),
      videoSeconds: parseInt(localStorage.getItem('veo_video_seconds') || '0'),
      apiCalls: parseInt(localStorage.getItem('api_call_count') || '0')
    };
  }
  
  /**
   * Track API usage for credit calculation
   */
  static trackUsage(type: 'text' | 'video', amount: number) {
    if (type === 'text') {
      const current = parseInt(localStorage.getItem('gemini_text_usage') || '0');
      localStorage.setItem('gemini_text_usage', (current + amount).toString());
    } else if (type === 'video') {
      const currentCount = parseInt(localStorage.getItem('veo_video_count') || '0');
      const currentSeconds = parseInt(localStorage.getItem('veo_video_seconds') || '0');
      localStorage.setItem('veo_video_count', (currentCount + 1).toString());
      localStorage.setItem('veo_video_seconds', (currentSeconds + amount).toString());
    }
    
    // Increment API call count
    const callCount = parseInt(localStorage.getItem('api_call_count') || '0');
    localStorage.setItem('api_call_count', (callCount + 1).toString());
  }
  
  /**
   * Set total credit amount (for configuration)
   */
  static setTotalCredit(amount: number) {
    localStorage.setItem('google_cloud_credit_total', amount.toString());
  }
  
  /**
   * Reset usage data (for testing)
   */
  static resetUsage() {
    localStorage.removeItem('gemini_text_usage');
    localStorage.removeItem('veo_video_count');
    localStorage.removeItem('veo_video_seconds');
    localStorage.removeItem('api_call_count');
    localStorage.removeItem(this.STORAGE_KEY);
  }
}